// One restroom on the facilities page: its queue (live, or the peak for a past game) with a
// queue-time sparkline, how close it is to its next cleaning (every CLEANING_THRESHOLD visits),
// and its visits. Restrooms without queue readings show a visits sparkline instead.
// Clicking opens the details.
import React from 'react';
import Icon from '../../../components/atoms/Icon';
import { Sparkline, unitFormatter } from '../../Concession/live/LiveComponents';
import RestroomIcon from './RestroomIcon';
import { VISITS_COLOR } from '../charts/VisitsChart';
import {
  CLEANING_THRESHOLD, cycleColor, visitFlow, minuteToClock, formatDueIn, formatRate, queueColor,
} from '../facilitiesMetrics';

// Visits per 5 minutes for the sparkline (restrooms without queue readings)
const SPARK_MINUTES = 5;

const RestroomCard = ({ stats, isLive, share, onOpen, toTime, range, gatesOpen, gameStart }) => {
  const {
    restroom, label, series, visits, dueCount, peak, cycle, rate, dueIn, stale, queue: queuePoints, hasQueue, queueNow, queuePeak,
  } = stats;
  const showQueue = hasQueue || restroom.hasQueueSensors;
  const queue = isLive ? queueNow : queuePeak;
  const qColor = queue ? queueColor(queue.value) : 'var(--q-text-subtle)';
  const cColor = cycleColor(cycle);

  const inRange = (start, end) => !range || (end > range.start && start < range.end);
  const spark = hasQueue
    ? queuePoints.filter((p) => inRange(p.minute, p.minute + 1)).map((p) => ({ t: toTime(p.minute).getTime(), y: p.queueTime }))
    : visitFlow(series, SPARK_MINUTES).filter((b) => inRange(b.start, b.end)).map((b) => ({ t: toTime(b.start).getTime(), y: b.visits }));
  const markers = [
    gatesOpen != null && { t: toTime(gatesOpen).getTime(), label: 'Gates open' },
    gameStart != null && { t: toTime(gameStart).getTime(), label: 'Game start' },
  ].filter(Boolean);
  const sparkline = (
    <div className="fx-rcard-spark">
      <Sparkline
        points={spark}
        color={hasQueue ? 'var(--q-text-subtle)' : VISITS_COLOR}
        valueColor={hasQueue ? queueColor : undefined}
        height={40}
        minMax={hasQueue ? 5 : 0}
        formatValue={unitFormatter(hasQueue ? 'min' : `entries / ${SPARK_MINUTES} min`)}
        markers={markers}
        markerLabel="none"
      />
    </div>
  );

  // Live: progress toward the next cleaning
  const cleaning = isLive && (
    <div className="fx-rcard-clean">
      <div className="fx-rcard-clean-top">
        <span className="fx-rcard-count-label">Next cleaning</span>
        <span className="fx-rcard-clean-value" style={{ color: cColor }}>
          {cycle.since}<span className="fx-rcard-unit">/ {CLEANING_THRESHOLD}</span>
        </span>
        {cycle.dueSoon && <span className="fx-soon-pill">Due soon</span>}
      </div>
      <div className="fx-progress" aria-hidden="true">
        <span style={{ width: `${Math.min(100, cycle.progress * 100)}%`, background: cColor }} />
      </div>
      <div className="fx-rcard-note">
        {stale
          ? 'No recent readings'
          : dueIn != null
            ? <>{cycle.toGo} entries to go · due in <strong>{formatDueIn(dueIn)}</strong> at this pace</>
            : <>{cycle.toGo} entries to go · no entries in the last 10 min</>}
      </div>
    </div>
  );

  return (
    <button type="button" className="fx-rcard" onClick={() => onOpen(stats)}>
      <div className="fx-rcard-head">
        <RestroomIcon imageURL={restroom.imageURL} className="fx-rcard-icon" />
        <span className="fx-rcard-title">
          <span className="fx-rcard-name">{label}</span>
          {restroom.area && label.indexOf(restroom.area) === -1 && <span className="fx-rcard-area">{restroom.area}</span>}
        </span>
        <Icon name="chevron-right" className="fx-rcard-chevron" />
      </div>

      {showQueue ? (
        <div className="fx-rcard-main">
          <div className="cx-metric-label">
            {queue && <span className="cx-status-dot" style={{ backgroundColor: qColor }} />}
            {isLive ? 'Queue now' : 'Peak queue'}
          </div>
          {queue ? (
            <div className="fx-rcard-row">
              <span className="fx-rcard-value" style={{ color: qColor }}>
                {queue.value}
                <span className="fx-rcard-unit">min</span>
              </span>
              {!isLive && <span className="fx-rcard-at">at {minuteToClock(queue.minute)}</span>}
            </div>
          ) : (
            <div className="fx-rcard-none">{hasQueue ? 'No recent reading' : 'No queue readings'}</div>
          )}
          {hasQueue && sparkline}
        </div>
      ) : (
        <div className="fx-rcard-main">
          <div className="cx-metric-label">{isLive ? 'Entries today' : 'Entries'}</div>
          <div className="fx-rcard-row">
            <span className="fx-rcard-value">{visits.toLocaleString()}</span>
            {peak && <span className="fx-rcard-at">Peak {minuteToClock(peak.start)}</span>}
          </div>
          {sparkline}
        </div>
      )}

      {cleaning}

      <div className="fx-rcard-counts">
        <div>
          <div className="fx-rcard-count-label">{isLive ? 'Entries today' : 'Entries'}</div>
          <div className="fx-rcard-count">{visits.toLocaleString()}</div>
          <div className="fx-rcard-rate">
            {isLive ? `${formatRate(rate)} / min now` : `${Math.round(share)}% of all`}
          </div>
        </div>
        <div>
          <div className="fx-rcard-count-label">Cleanings due</div>
          <div className="fx-rcard-count">{dueCount}</div>
          <div className="fx-rcard-rate">Every {CLEANING_THRESHOLD} entries</div>
        </div>
      </div>
    </button>
  );
};

export default RestroomCard;
